/**
 * useMutation hooks for the report-embed content writes:
 * `updateDataFrequency` (refresh-setting) and `deleteReportEmbeds`. These two
 * are the most intricate writes in the vertical — they rewrite the embedding
 * stories' HTML content (DOM parse → mutate `.has-media` blocks → re-serialize)
 * and then persist via GraphQL.
 *
 * Replaces `publicationViewEffects.updateDataFrequency` + `deleteReportEmbeds`.
 *
 * Auth (§1): GraphQL via `postQuery` (cookie), embed-service via `transport`.
 * Toast copy passed in (§8). Both invalidate `reportEmbeds` + `publication`
 * on settle so the listing + updatedAt refresh — replacing the effects'
 * explicit `setPublicationsReportsStatus` + `updatePublicationUpdatedAt`
 * re-dispatches. The publication `updatedAt` bump is written inline
 * (mirroring the effects' chained `updatePublicationUpdatedAt`).
 *
 * NOTE — faithful port: the retired `updateDataFrequency` fired the embed-
 * service `updateEmbeds` POST WITHOUT awaiting it, so `failedEmbeds` was
 * effectively always empty when the subsequent payload was computed. That
 * (pre-existing) behavior is preserved exactly; do not "fix" it here.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { transport } from '@opengov/app-core/transport';
import { DATE_FORMAT_WITH_TIMEZONE } from '@opengov/stories-shared/constants';
import { updatePublicationUpdatedAt } from '@opengov/stories-shared/graphQL';
import { IStoryContent, IUpdatePayload } from '@opengov/stories-shared/types';
import moment from 'moment';

import { postQuery } from '../../../../effects/publications/postQuery';
import {
  deleteStoryEmbedByStoryAndEmbedId,
  updateStoryContentByIds,
  updateStoryEmbedByStoryAndEmbedId
} from '../../../../graphQL/queries';
import { getStoryContentsByEmbedIds } from './embedContentUtils';
import { getUserById } from '../../../../routes/hasura';
import { getReportType } from '../../../../utils';
import { useToast } from '../../../Toast/context/useToast.context';
import { EMBED_SYNC_SETTING, TAG_DIV } from '../../../../constants';
import { IStoryEmbedDeletePayload } from '../../../../../types';
import { publicationViewQueryKeys } from './publicationViewQueryKeys';

const REPORT_EMBEDS_KEY = [...publicationViewQueryKeys.all, 'reportEmbeds'];
const PUBLICATION_KEY = [...publicationViewQueryKeys.all, 'publication'];

function parseResponse(message: string): string[] {
  try {
    return [...JSON.parse(message).data];
  } catch {
    return [];
  }
}

export async function bumpPublicationUpdatedAt(publicationId: string, userId: number): Promise<void> {
  const authorId = await getUserById(userId);
  const formattedDate = moment(new Date()).format(DATE_FORMAT_WITH_TIMEZONE);
  await postQuery(updatePublicationUpdatedAt, { publicationId, updatedAt: formattedDate, userId: authorId });
}

// ---------------------------------------------------------------------------
// updateDataFrequency
// ---------------------------------------------------------------------------

export type UpdateDataFrequencyMessages = {
  somethingWentWrongError: string;
  unableToBulkUpdateReportEmbeds: string;
  refreshOnUpdate: string;
  refreshEmbedsFailure: string;
  refreshEmbedsFailureDescription: string;
  refreshEmbedsSuccess: string;
  refreshEmbedsSuccessDescription: (args: { type: string }) => string;
};

export type UpdateDataFrequencyInput = {
  updatePayload: IUpdatePayload[];
  frequency: string;
  publicationId: string;
  userId: number;
};

export function useUpdateDataFrequency(messages: UpdateDataFrequencyMessages) {
  const queryClient = useQueryClient();
  const { toggleToast } = useToast();

  return useMutation<null | string[], Error, UpdateDataFrequencyInput>({
    mutationFn: async ({ updatePayload, frequency, publicationId, userId }) => {
      const embedUpdatePayload = updatePayload.map((payload) => ({
        embedId: payload.embedId,
        shouldSync: frequency === EMBED_SYNC_SETTING.LIVE
      }));

      const failedEmbeds: string[] = [];
      if (embedUpdatePayload.length > 0) {
        // Not awaited — preserves the retired effect's behavior exactly.
        transport
          .url(`/api/embed_service/v3/embeds/updateEmbeds`)
          .json({ embeds: embedUpdatePayload })
          .post()
          .json()
          .catch(async (e) => {
            const parsedError = parseResponse(e.message);
            if (parsedError.length > 0) {
              failedEmbeds.push(...parsedError);
            } else {
              toggleToast({
                title: messages.somethingWentWrongError,
                body: messages.unableToBulkUpdateReportEmbeds,
                severity: 'error'
              });
              return e;
            }
          });
      } else {
        toggleToast({
          title: messages.somethingWentWrongError,
          body: messages.unableToBulkUpdateReportEmbeds,
          severity: 'error'
        });
        return null;
      }

      const finalUpdatePayload = updatePayload.filter((payload) => !failedEmbeds.includes(payload.embedId));
      const storyIds = finalUpdatePayload.map((e) => e.storyId);
      const embedIds = finalUpdatePayload.map((e) => e.embedId);
      try {
        let storyContents = (await getStoryContentsByEmbedIds(finalUpdatePayload, true)) as IStoryContent[];
        storyContents = storyContents.filter((content) => storyIds.includes(content.storyId));
        const updateContents: { id: string; content: string }[] = [];

        storyContents.forEach((storyContent) => {
          const htmlMap = new Map<string, string>([]);
          const fragment = document.createRange().createContextualFragment(storyContent.content);
          const mediaBlocks = fragment.querySelectorAll('.has-media');
          mediaBlocks.forEach((mediaBlock) => {
            for (let i = 0; i < mediaBlock.children.length; i++) {
              if (mediaBlock.children[i].tagName === TAG_DIV && mediaBlock.children[i].hasAttribute('data-embed-id')) {
                const embedId = mediaBlock.children[i].getAttribute('data-embed-id');
                if (embedIds.includes(embedId)) {
                  mediaBlock.children[i].setAttribute('data-update-setting', frequency);
                  htmlMap.set(mediaBlock.getAttribute('data-media-index'), mediaBlock.innerHTML);
                }
              }
            }
          });
          for (const [index, innerHTML] of htmlMap) {
            fragment.querySelector(`.has-media[data-media-index="${index}"]`).innerHTML = innerHTML;
          }
          const element = document.createElement('div');
          element.appendChild(fragment);
          updateContents.push({ id: storyContent._id, content: JSON.stringify(element.innerHTML) });
        });
        if (updateContents.length === 0) return null;
        await postQuery(updateStoryContentByIds(updateContents), {});
        await postQuery(updateStoryEmbedByStoryAndEmbedId(finalUpdatePayload, frequency), {});
        await bumpPublicationUpdatedAt(publicationId, userId);

        const frequencyType =
          frequency === EMBED_SYNC_SETTING.REFRESH_ON_UPDATE ? messages.refreshOnUpdate : frequency;
        if (failedEmbeds.length) {
          toggleToast({
            title: messages.refreshEmbedsFailure,
            body: messages.refreshEmbedsFailureDescription,
            severity: 'error'
          });
          return failedEmbeds;
        }
        toggleToast({
          title: messages.refreshEmbedsSuccess,
          body: messages.refreshEmbedsSuccessDescription({ type: frequencyType }),
          severity: 'success'
        });
      } catch {
        toggleToast({
          title: messages.somethingWentWrongError,
          body: messages.unableToBulkUpdateReportEmbeds,
          severity: 'error'
        });
      }
      return null;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: REPORT_EMBEDS_KEY });
      void queryClient.invalidateQueries({ queryKey: PUBLICATION_KEY });
    }
  });
}

// ---------------------------------------------------------------------------
// deleteReportEmbeds
// ---------------------------------------------------------------------------

export type DeleteReportEmbedsMessages = {
  somethingWentWrongError: string;
  unableToBulkDeleteReportEmbeds: string;
  report: string;
  reports: string;
  deleted: string;
  failedToDeleteType: string;
  failedToUpdateType: string;
  failedToUpdateBody: string;
  reportsDeletedBody: (args: { value: number }) => string;
};

export function useDeleteReportEmbeds(messages: DeleteReportEmbedsMessages) {
  const queryClient = useQueryClient();
  const { toggleToast } = useToast();

  return useMutation<void, Error, IStoryEmbedDeletePayload>({
    mutationFn: async ({ deletePayload, publicationId, userId, isReportEmbedListSelected, type }) => {
      const storyIds = deletePayload.map((e) => e.storyId);
      const embedIds = deletePayload.map((e) => e.embedId);

      const fireDeleteToast = (hasErrors: boolean) => {
        if (isReportEmbedListSelected) {
          if (!hasErrors) {
            toggleToast({ title: `${messages.report} ${getReportType(type)} ${messages.deleted}`, severity: 'success' });
          } else {
            toggleToast({
              title: `${messages.failedToUpdateType} ${messages.report} ${type}`,
              severity: 'error'
            });
          }
        } else if (!hasErrors) {
          toggleToast({
            title: `${messages.reports} ${messages.deleted}`,
            body: messages.reportsDeletedBody({ value: deletePayload.length }),
            severity: 'success'
          });
        } else {
          toggleToast({ title: messages.failedToDeleteType, body: messages.failedToUpdateBody, severity: 'error' });
        }
      };

      try {
        let storyContents = (await getStoryContentsByEmbedIds(deletePayload, true)) as IStoryContent[];
        storyContents = storyContents.filter((content) => storyIds.includes(content.storyId));
        if (storyContents.length > 0) {
          const updateContents: { id: string; content: string }[] = [];
          storyContents.forEach((storyContent) => {
            const htmlMap = new Map<string, string>([]);
            const fragment = document.createRange().createContextualFragment(storyContent.content);
            const mediaBlocks = fragment.querySelectorAll('.has-media');
            let updating = false;
            if (mediaBlocks.length === 0) return;
            mediaBlocks.forEach((mediaBlock) => {
              for (let i = 0; i < mediaBlock.children.length; i++) {
                if (mediaBlock.children[i].tagName === TAG_DIV && mediaBlock.children[i].hasAttribute('data-embed-id')) {
                  const embedId = mediaBlock.children[i].getAttribute('data-embed-id');
                  if (embedIds.includes(embedId)) {
                    updating = true;
                    mediaBlock.replaceChildren();
                    mediaBlock.removeAttribute('data-media-type');
                    htmlMap.set(mediaBlock.getAttribute('data-media-index'), mediaBlock.innerHTML);
                  }
                }
              }
            });
            if (updating) {
              for (const [index, innerHTML] of htmlMap) {
                fragment.querySelector(`.has-media[data-media-index="${index}"]`).innerHTML = innerHTML;
                fragment.querySelector(`.has-media[data-media-index="${index}"]`).className = 'media-block';
              }
              const element = document.createElement('div');
              element.appendChild(fragment);
              updateContents.push({ id: storyContent._id, content: JSON.stringify(element.innerHTML) });
            }
          });
          if (updateContents.length === 0) {
            const deleteResponse = await postQuery(deleteStoryEmbedByStoryAndEmbedId(deletePayload), {});
            fireDeleteToast(!!deleteResponse.errors);
            return;
          }
          await postQuery(updateStoryContentByIds(updateContents), {});
        }
        const deleteResponse = await postQuery(deleteStoryEmbedByStoryAndEmbedId(deletePayload), {});
        fireDeleteToast(!!deleteResponse.errors);
        await bumpPublicationUpdatedAt(publicationId, userId);
      } catch {
        toggleToast({
          title: messages.somethingWentWrongError,
          body: messages.unableToBulkDeleteReportEmbeds,
          severity: 'error'
        });
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: REPORT_EMBEDS_KEY });
      void queryClient.invalidateQueries({ queryKey: PUBLICATION_KEY });
    }
  });
}
